import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import {
  MdEdit,
  MdPersonAddAlt1,
  MdVisibility,
  MdRefresh,
  MdEditDocument,
  MdSort,
  MdFilterList,
  MdSearch,
  MdClear,
  MdKeyboardArrowUp,
  MdKeyboardArrowDown,
  MdPeople,
  MdCalendarToday,
} from "react-icons/md";

const ArticleTable = ({
  currentItems,
  getArticleTitle,
  getArticleAuthor,
  getArticleDate,
  handleActionPrimary,
  handleViewDetails,
  handleEditArticle,
  handleContentArticle,
  handleReloadData,
  isLoading,
  actionPrimaryText = "Affecter",
  actionPrimaryIcon,
  showEditButton = true,
  showPrimaryButton = true,
  showContentButton = false,
  showReloadButton = false,
  showEvaluatorsColumn = true,
  dateColumnLabel = "Date de soumission",
  showStatusButton = false,
  handleStatusArticle,
}) => {
  const ActionIcon = actionPrimaryIcon || MdPersonAddAlt1;

  // États pour le tri et le filtrage
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [filters, setFilters] = useState({
    search: "",
    domain: "",
    author: "",
    dateFrom: "",
    dateTo: "",
    evaluator: "",
  });
  const [showFilters, setShowFilters] = useState(false);

  // Fonction pour convertir une date française en objet Date
  const parseFrenchDate = (dateString) => {
    if (!dateString) return null;

    // Mapping des mois français
    const frenchMonths = {
      janvier: "01",
      jan: "01",
      février: "02",
      fév: "02",
      fevrier: "02",
      mars: "03",
      mar: "03",
      avril: "04",
      avr: "04",
      mai: "05",
      juin: "06",
      juillet: "07",
      juil: "07",
      août: "08",
      aout: "08",
      septembre: "09",
      sept: "09",
      sep: "09",
      octobre: "10",
      oct: "10",
      novembre: "11",
      nov: "11",
      décembre: "12",
      déc: "12",
      decembre: "12",
      dec: "12",
    };

    try {
      // Si c'est déjà une date ISO ou standard, on l'utilise directement
      const standardDate = new Date(dateString);
      if (!isNaN(standardDate.getTime()) && dateString.includes("-")) {
        return standardDate;
      }

      // Pattern pour "23 mai 2025" ou "23/05/2025" etc.
      const frenchPattern =
        /(\d{1,2})\s*(janvier|février|mars|avril|mai|juin|juillet|août|septembre|octobre|novembre|décembre|jan|fév|mar|avr|juil|sept|oct|nov|déc|fevrier|aout|decembre)\s*(\d{4})/i;
      const match = dateString.toLowerCase().match(frenchPattern);

      if (match) {
        const day = match[1].padStart(2, "0");
        const monthName = match[2].toLowerCase();
        const year = match[3];
        const month = frenchMonths[monthName];

        if (month) {
          // Créer la date au format ISO
          return new Date(`${year}-${month}-${day}`);
        }
      }

      // Pattern pour formats numériques DD/MM/YYYY ou DD-MM-YYYY
      const numericPattern = /(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})/;
      const numericMatch = dateString.match(numericPattern);

      if (numericMatch) {
        const day = numericMatch[1].padStart(2, "0");
        const month = numericMatch[2].padStart(2, "0");
        const year = numericMatch[3];
        return new Date(`${year}-${month}-${day}`);
      }

      // Fallback: essayer de parser directement
      return new Date(dateString);
    } catch (error) {
      console.warn("Erreur lors du parsing de la date:", dateString, error);
      return null;
    }
  };

  // Fonction pour formater une date ISO en format lisible
  const formatDate = (dateString) => {
    if (!dateString) return "Non spécifiée";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      return "Date invalide";
    }
  };

  // Fonction pour obtenir les noms des évaluateurs (corrigée pour utiliser reviewers)
  const getEvaluateursNames = (article) => {
    if (!article.reviewers || article.reviewers.length === 0) {
      return "Aucun évaluateur assigné";
    }
    return article.reviewers
      .map(
        (reviewer) =>
          `${reviewer.grade_rev || ""} ${reviewer.first_name || ""} ${reviewer.last_name || ""
          }`
      )
      .join(", ");
  };

  // Fonction de tri
  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // Fonction pour obtenir l'icône de tri
  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <MdSort className="h-4 w-4 text-gray-400" />;
    }
    return sortConfig.direction === "asc" ? (
      <MdKeyboardArrowUp className="h-4 w-4 text-[#57A863]" />
    ) : (
      <MdKeyboardArrowDown className="h-4 w-4 text-[#57A863]" />
    );
  };

  // Fonction pour obtenir la date d'évaluation (avec fallback)
  const getEvalDate = (article) => {
    // Essayer plusieurs champs possibles pour la date d'évaluation
    return (
      article.date_eval_art ||
      article.date_evaluation ||
      article.eval_date ||
      null
    );
  };

  // Vérifier si au moins un article a une date d'évaluation
  const hasEvalDates = useMemo(() => {
    return currentItems.some((article) => !!getEvalDate(article));
  }, [currentItems]);

  // Fonction pour obtenir les rubriques d'un article comme une chaîne formatée
  const getArticleRubriques = (article) => {
    if (!article.rubriques || article.rubriques.length === 0) {
      return "Non spécifié";
    }
    return article.rubriques.map((rub) => rub.name_rub).join(", ");
  };

  // Fonction pour filtrer et trier les données
  const filteredAndSortedItems = useMemo(() => {
    let filtered = [...currentItems];

    // Application des filtres
    if (filters.search) {
      filtered = filtered.filter(
        (article) =>
          getArticleTitle(article)
            ?.toLowerCase()
            .includes(filters.search.toLowerCase()) ||
          getArticleAuthor(article)
            ?.toLowerCase()
            .includes(filters.search.toLowerCase())
      );
    }

    if (filters.domain) {
      filtered = filtered.filter((article) => {
        if (!article.rubriques || article.rubriques.length === 0) return false;
        return article.rubriques.some((rubrique) =>
          rubrique.name_rub
            ?.toLowerCase()
            .includes(filters.domain.toLowerCase())
        );
      });
    }

    if (filters.author) {
      filtered = filtered.filter((article) =>
        getArticleAuthor(article)
          ?.toLowerCase()
          .includes(filters.author.toLowerCase())
      );
    }

    // Filtre pour les évaluateurs (corrigé pour utiliser reviewers)
    if (filters.evaluator) {
      filtered = filtered.filter((article) => {
        if (!article.reviewers) return false;

        return article.reviewers.some((reviewer) => {
          const evaluatorFullName = `${reviewer.grade_rev || ""} ${reviewer.first_name || ""
            } ${reviewer.last_name || ""}`.toLowerCase();
          return evaluatorFullName.includes(filters.evaluator.toLowerCase());
        });
      });
    }

    // Filtre par date - CORRIGÉ
    if (filters.dateFrom) {
      filtered = filtered.filter((article) => {
        const articleDateString = getArticleDate(article);
        const articleDate = parseFrenchDate(articleDateString);
        const fromDate = new Date(filters.dateFrom);

        if (!articleDate || isNaN(articleDate.getTime())) {
          return false; // Exclure les articles avec des dates invalides
        }

        // Comparer seulement les dates (ignorer l'heure)
        const articleDateOnly = new Date(
          articleDate.getFullYear(),
          articleDate.getMonth(),
          articleDate.getDate()
        );
        const fromDateOnly = new Date(
          fromDate.getFullYear(),
          fromDate.getMonth(),
          fromDate.getDate()
        );

        return articleDateOnly >= fromDateOnly;
      });
    }

    if (filters.dateTo) {
      filtered = filtered.filter((article) => {
        const articleDateString = getArticleDate(article);
        const articleDate = parseFrenchDate(articleDateString);
        const toDate = new Date(filters.dateTo);

        if (!articleDate || isNaN(articleDate.getTime())) {
          return false; // Exclure les articles avec des dates invalides
        }

        // Comparer seulement les dates (ignorer l'heure)
        const articleDateOnly = new Date(
          articleDate.getFullYear(),
          articleDate.getMonth(),
          articleDate.getDate()
        );
        const toDateOnly = new Date(
          toDate.getFullYear(),
          toDate.getMonth(),
          toDate.getDate()
        );

        return articleDateOnly <= toDateOnly;
      });
    }

    // Application du tri - CORRIGÉ pour les dates et rubriques
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue, bValue;

        switch (sortConfig.key) {
          case "title":
            aValue = getArticleTitle(a)?.toLowerCase() || "";
            bValue = getArticleTitle(b)?.toLowerCase() || "";
            break;
          case "author":
            aValue = getArticleAuthor(a)?.toLowerCase() || "";
            bValue = getArticleAuthor(b)?.toLowerCase() || "";
            break;
          case "domain":
            aValue =
              a.rubriques && a.rubriques.length > 0
                ? a.rubriques[0].name_rub?.toLowerCase() || ""
                : "";
            bValue =
              b.rubriques && b.rubriques.length > 0
                ? b.rubriques[0].name_rub?.toLowerCase() || ""
                : "";
            break;
          case "date": {
            const dateA = parseFrenchDate(getArticleDate(a));
            const dateB = parseFrenchDate(getArticleDate(b));
            aValue = dateA && !isNaN(dateA) ? dateA.getTime() : -Infinity;
            bValue = dateB && !isNaN(dateB) ? dateB.getTime() : -Infinity;
            break;
          }
          case "evalDate": {
            const dateA = getEvalDate(a) ? new Date(getEvalDate(a)) : null;
            const dateB = getEvalDate(b) ? new Date(getEvalDate(b)) : null;
            aValue = dateA && !isNaN(dateA) ? dateA.getTime() : -Infinity;
            bValue = dateB && !isNaN(dateB) ? dateB.getTime() : -Infinity;
            break;
          }
          case "evaluators":
            aValue = a.reviewers?.length || 0;
            bValue = b.reviewers?.length || 0;
            break;
          default:
            return 0;
        }

        if (aValue < bValue) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  }, [
    currentItems,
    filters,
    sortConfig,
    getArticleTitle,
    getArticleAuthor,
    getArticleDate,
  ]);

  // Fonction pour réinitialiser les filtres
  const clearFilters = () => {
    setFilters({
      search: "",
      domain: "",
      author: "",
      dateFrom: "",
      dateTo: "",
      evaluator: "",
    });
    setSortConfig({ key: null, direction: "asc" });
  };

  // Obtenir les rubriques uniques pour le filtre
  const uniqueDomains = useMemo(() => {
    // Extraire toutes les rubriques de tous les articles
    const allRubriques = currentItems
      .flatMap((article) =>
        article.rubriques ? article.rubriques.map((rub) => rub.name_rub) : []
      )
      .filter((name) => name);

    // Créer un ensemble pour éliminer les doublons
    return [...new Set(allRubriques)];
  }, [currentItems]);

  // Calculer le nombre de colonnes pour le colspan
  const getColspan = () => {
    let count = 6; // Colonnes de base: titre, auteur, catégorie, date, actions
    if (hasEvalDates) count++;
    if (showEvaluatorsColumn) count++;
    return count;
  };

  return (
    <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
      {/* Header avec contrôles */}
      <div className="border-b border-gray-100 bg-gradient-to-r from-[#57A863]/5 to-[#4a9154]/5 px-6 py-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-4">
            <h3 className="text-lg font-semibold text-gray-900">Articles</h3>
            <span className="rounded-full border border-[#57A863]/20 bg-[#57A863]/10 px-3 py-1 text-sm font-medium text-[#57A863]">
              {filteredAndSortedItems.length} résultat
              {filteredAndSortedItems.length > 1 ? "s" : ""}
            </span>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row">
            {/* Barre de recherche */}
            <div className="relative">
              <MdSearch className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher un article ou auteur..."
                value={filters.search}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, search: e.target.value }))
                }
                className="w-full rounded-lg border border-gray-200 py-2 pl-10 pr-4 text-sm transition-all duration-200 focus:border-[#57A863] focus:outline-none focus:ring-2 focus:ring-[#57A863]/50 sm:w-64"
              />
              {filters.search && (
                <button
                  onClick={() =>
                    setFilters((prev) => ({ ...prev, search: "" }))
                  }
                  className="absolute right-3 top-1/2 -translate-y-1/2 transform text-gray-400 hover:text-gray-600"
                >
                  <MdClear className="h-4 w-4" />
                </button>
              )}
            </div>

            <div className="flex gap-2">
              {/* Bouton Filtres */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-all duration-200 ${showFilters
                  ? "border-[#57A863]/30 bg-[#57A863]/10 text-[#57A863]"
                  : "border-gray-200 bg-white text-gray-700 hover:border-[#57A863]/20 hover:bg-[#57A863]/5"
                  }`}
              >
                <MdFilterList className="h-4 w-4" />
                Filtres
              </button>

              {/* Bouton de rechargement */}
              {showReloadButton && (
                <button
                  onClick={handleReloadData}
                  disabled={isLoading}
                  className="group flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-all duration-200 hover:border-[#57A863]/20 hover:bg-[#57A863]/5 focus:outline-none focus:ring-2 focus:ring-[#57A863]/50 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <MdRefresh
                    className={`h-4 w-4 transition-transform duration-200 ${isLoading ? "animate-spin" : "group-hover:rotate-180"
                      }`}
                  />
                  {isLoading ? "Actualisation..." : "Actualiser"}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Panel de filtres avancés */}
        {showFilters && (
          <div className="mt-4 rounded-lg border border-[#57A863]/20 bg-white p-4 shadow-sm">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
              <div>
                <label
                  htmlFor="domain_id"
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  Catégorie
                </label>
                <select
                  id="domain_id"
                  value={filters.domain}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, domain: e.target.value }))
                  }
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm transition-all duration-200 focus:border-[#57A863] focus:outline-none focus:ring-2 focus:ring-[#57A863]/50"
                >
                  <option value="">Toutes les catégories</option>
                  {uniqueDomains.map((domain) => (
                    <option key={domain} value={domain}>
                      {domain}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  htmlFor="nom_auteur"
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  Auteur
                </label>
                <input
                  type="text"
                  id="nom_auteur"
                  placeholder="Nom de l'auteur"
                  value={filters.author}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, author: e.target.value }))
                  }
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm transition-all duration-200 focus:border-[#57A863] focus:outline-none focus:ring-2 focus:ring-[#57A863]/50"
                />
              </div>

              {/* Champ pour filtrer par évaluateur */}
              {showEvaluatorsColumn && (
                <div>
                  <label
                    htmlFor="evaluator"
                    className="mb-1 block text-sm font-medium text-gray-700"
                  >
                    Évaluateur
                  </label>
                  <input
                    type="text"
                    id="evaluator"
                    placeholder="Nom de l'évaluateur"
                    value={filters.evaluator}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        evaluator: e.target.value,
                      }))
                    }
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm transition-all duration-200 focus:border-[#57A863] focus:outline-none focus:ring-2 focus:ring-[#57A863]/50"
                  />
                </div>
              )}

              <div>
                <label
                  htmlFor="dateFrom"
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  Date de début
                </label>
                <input
                  id="dateFrom"
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      dateFrom: e.target.value,
                    }))
                  }
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm transition-all duration-200 focus:border-[#57A863] focus:outline-none focus:ring-2 focus:ring-[#57A863]/50"
                />
              </div>

              <div>
                <label
                  htmlFor="date_fin"
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  Date de fin
                </label>
                <input
                  type="date"
                  id="date_fin"
                  value={filters.dateTo}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, dateTo: e.target.value }))
                  }
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm transition-all duration-200 focus:border-[#57A863] focus:outline-none focus:ring-2 focus:ring-[#57A863]/50"
                />
              </div>
            </div>

            <div className="mt-4 flex justify-end">
              <button
                onClick={clearFilters}
                className="flex items-center gap-2 rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-600 transition-colors duration-200 hover:bg-gray-200"
              >
                <MdClear className="h-4 w-4" />
                Réinitialiser
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[800px]">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50/50">
              <th className="px-6 py-4 text-left">
                <button
                  onClick={() => handleSort("title")}
                  className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-500 transition-colors duration-200 hover:text-[#57A863]"
                >
                  Titre
                  {getSortIcon("title")}
                </button>
              </th>
              <th className="px-6 py-4 text-left">
                <button
                  onClick={() => handleSort("author")}
                  className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-500 transition-colors duration-200 hover:text-[#57A863]"
                >
                  Auteur
                  {getSortIcon("author")}
                </button>
              </th>
              <th className="px-6 py-4 text-left">
                <button
                  onClick={() => handleSort("domain")}
                  className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-500 transition-colors duration-200 hover:text-[#57A863]"
                >
                  Catégorie
                  {getSortIcon("domain")}
                </button>
              </th>
              <th className="px-6 py-4 text-left">
                <button
                  onClick={() => handleSort("date")}
                  className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-500 transition-colors duration-200 hover:text-[#57A863]"
                >
                  {dateColumnLabel}
                  {getSortIcon("date")}
                </button>
              </th>
              {/* Date d'évaluation - n'afficher que si au moins un article a une date d'évaluation */}
              {hasEvalDates && (
                <th className="px-6 py-4 text-left">
                  <button
                    onClick={() => handleSort("evalDate")}
                    className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-500 transition-colors duration-200 hover:text-[#57A863]"
                  >
                    Date d'évaluation
                    {getSortIcon("evalDate")}
                  </button>
                </th>
              )}
              {/* Évaluateurs - conditionnel */}
              {showEvaluatorsColumn && (
                <th className="px-6 py-4 text-left">
                  <button
                    onClick={() => handleSort("evaluators")}
                    className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-500 transition-colors duration-200 hover:text-[#57A863]"
                  >
                    Évaluateurs
                    {getSortIcon("evaluators")}
                  </button>
                </th>
              )}
              <th className="px-6 py-4 text-left">
                <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Actions
                </span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {filteredAndSortedItems.length > 0 ? (
              filteredAndSortedItems.map((article, index) => (
                <tr
                  key={article.id_art}
                  className="hover:bg-[#57A863]/3 group transition-colors duration-150"
                >
                  <td className="px-6 py-4">
                    <div className="flex items-start">
                      <div className="flex-1">
                        <p className="line-clamp-2 text-sm font-medium text-gray-900 transition-colors duration-150 group-hover:text-[#57A863]">
                          {getArticleTitle(article)}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#57A863] to-[#4a9154] shadow-sm">
                        <span className="text-xs font-semibold text-white">
                          {getArticleAuthor(article)
                            ?.charAt(0)
                            ?.toUpperCase() || "?"}
                        </span>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900">
                          {getArticleAuthor(article)}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {article.rubriques && article.rubriques.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {article.rubriques.map((rubrique, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center rounded-full border border-[#57A863]/20 bg-[#57A863]/10 px-2.5 py-1 text-xs font-medium text-[#57A863]"
                          >
                            {rubrique.name_rub || "Non spécifié"}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="inline-flex items-center rounded-full border border-[#57A863]/20 bg-[#57A863]/10 px-2.5 py-1 text-xs font-medium text-[#57A863]">
                        Non spécifié
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-start gap-2">
                      <MdCalendarToday className="mt-0.5 h-4 w-4 text-[#57A863]" />
                      <p className="text-sm text-gray-600">
                        {getArticleDate(article)
                          ? new Date(getArticleDate(article)).toLocaleDateString("fr-FR", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })
                          : "Non spécifiée"}
                      </p>
                    </div>
                  </td>
                  {/* Date d'évaluation - n'afficher que si au moins un article a une date d'évaluation */}
                  {hasEvalDates && (
                    <td className="px-6 py-4">
                      {getEvalDate(article) ? (
                        <div className="flex items-start gap-2">
                          <MdCalendarToday className="mt-0.5 h-4 w-4 text-[#57A863]" />
                          <p className="text-sm text-gray-600">
                            {formatDate(getEvalDate(article))}
                          </p>
                        </div>
                      ) : (
                        <span className="text-sm italic text-gray-500">
                          Non évalué
                        </span>
                      )}
                    </td>
                  )}
                  {/* Évaluateurs - conditionnel */}
                  {showEvaluatorsColumn && (
                    <td className="px-6 py-4">
                      <div className="flex items-start gap-2">
                        <MdPeople className="mt-0.5 h-4 w-4 text-[#57A863]" />
                        {article.reviewers && article.reviewers.length > 0 ? (
                          <p
                            className="line-clamp-2 max-w-[200px] text-sm text-gray-600"
                            title={getEvaluateursNames(article)}
                          >
                            <span className="font-medium text-[#57A863]">
                              {article.reviewers.length}
                            </span>
                            <span className="mx-1">•</span>
                            {getEvaluateursNames(article)}
                          </p>
                        ) : (
                          <p className="text-sm italic text-gray-500">
                            Aucun évaluateur assigné
                          </p>
                        )}
                      </div>
                    </td>
                  )}
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      {showPrimaryButton && (
                        <button
                          onClick={() => handleActionPrimary(article)}
                          className="group/btn inline-flex items-center gap-1.5 rounded-lg bg-[#57A863] px-3 py-2 text-xs font-medium text-white shadow-sm transition-all duration-200 hover:bg-[#4a9154] hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#57A863]/50 focus:ring-offset-1"
                        >
                          <ActionIcon className="h-3.5 w-3.5 transition-transform duration-200 group-hover/btn:scale-110" />
                          {actionPrimaryText}
                        </button>
                      )}

                      {showEditButton && (
                        <button
                          onClick={() => handleEditArticle(article)}
                          className="group/btn inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-xs font-medium text-white shadow-sm transition-all duration-200 hover:bg-blue-700 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                        >
                          <MdEdit className="h-3.5 w-3.5 transition-transform duration-200 group-hover/btn:scale-110" />
                          Modifier
                        </button>
                      )}

                      {showContentButton && (
                        <button
                          onClick={() => handleContentArticle(article)}
                          className="group/btn inline-flex items-center gap-1.5 rounded-lg bg-gray-100 px-3 py-2 text-xs font-medium text-gray-700 shadow-sm transition-all duration-200 hover:bg-gray-200 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-1"
                        >
                          <MdEditDocument className="h-3.5 w-3.5 transition-transform duration-200 group-hover/btn:scale-110" />
                          Document
                        </button>
                      )}

                      {showStatusButton && (
                        <button
                          onClick={() => handleStatusArticle(article)}
                          className="group/btn inline-flex items-center gap-1.5 rounded-lg bg-[#57A863] px-3 py-2 text-xs font-medium text-white shadow-sm transition-all duration-200 hover:bg-[#4a9154] hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#57A863]/50 focus:ring-offset-1"
                        >
                          <MdEditDocument className="h-3.5 w-3.5 transition-transform duration-200 group-hover/btn:scale-110" />
                          Attribuer
                        </button>
                      )}

                      <button
                        onClick={() => handleViewDetails(article)}
                        className="group/btn inline-flex items-center gap-1.5 rounded-lg bg-gray-100 px-3 py-2 text-xs font-medium text-gray-700 shadow-sm transition-all duration-200 hover:bg-gray-200 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-1"
                      >
                        <MdVisibility className="h-3.5 w-3.5 transition-transform duration-200 group-hover/btn:scale-110" />
                        Détails
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={getColspan()} className="px-6 py-16 text-center">
                  {isLoading ? (
                    <div className="flex flex-col items-center justify-center space-y-3">
                      <div className="relative">
                        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-[#57A863]"></div>
                      </div>
                      <p className="text-sm font-medium text-gray-500">
                        Chargement des données...
                      </p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center space-y-3">
                      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#57A863]/10">
                        <svg
                          className="h-8 w-8 text-[#57A863]"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                      </div>
                      <p className="text-sm font-medium text-gray-500">
                        {Object.values(filters).some((filter) => filter !== "")
                          ? "Aucun résultat trouvé"
                          : "Aucun article trouvé"}
                      </p>
                      <p className="text-xs text-gray-400">
                        {Object.values(filters).some((filter) => filter !== "")
                          ? "Essayez de modifier vos critères de recherche"
                          : "Il n'y a actuellement aucun article à afficher"}
                      </p>
                    </div>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

ArticleTable.propTypes = {
  currentItems: PropTypes.array.isRequired,
  getArticleTitle: PropTypes.func.isRequired,
  getArticleAuthor: PropTypes.func.isRequired,
  getArticleDate: PropTypes.func.isRequired,
  handleActionPrimary: PropTypes.func,
  handleViewDetails: PropTypes.func.isRequired,
  handleEditArticle: PropTypes.func,
  handleContentArticle: PropTypes.func,
  handleReloadData: PropTypes.func,
  isLoading: PropTypes.bool,
  actionPrimaryText: PropTypes.string,
  actionPrimaryIcon: PropTypes.elementType,
  showEditButton: PropTypes.bool,
  showPrimaryButton: PropTypes.bool,
  showContentButton: PropTypes.bool,
  showReloadButton: PropTypes.bool,
  showEvaluatorsColumn: PropTypes.bool,
  dateColumnLabel: PropTypes.string,
};

export default ArticleTable;
